import { Injectable } from '@angular/core';
import { ServiceType } from '@web-frontend/microfrontends/mf-accounting-service/mf-accounting-service.component';
import { CompanyQuota, CheckTrialResponse } from '../permissions';
import { HttpClient } from '@angular/common/http';
import { SubscriptionViewService } from '../subscription-view';

export enum SubscriptionUpdateReason {
  seats = 'seats',
  billing = 'billing_documents',
}

@Injectable({
  providedIn: 'root',
})
export class SubscriptionService {
  constructor(
    private readonly http: HttpClient,
    private readonly subscriptionViewService: SubscriptionViewService
  ) {}

  checkSubscriptionQuota(
    quotas: CompanyQuota[] = [],
    resource: SubscriptionUpdateReason
  ): boolean {
    const quota = quotas.find((q) => q.resource === resource);
    return !quota || !quota.limit || quota.limit > quota.current;
  }

  canAddSeats(): Promise<{ value: boolean; serviceType: ServiceType }> {
    const url = `:API_BASE_URLsubscriptions/subscriptions`;
    return this.http
      .get<{ quotas?: CompanyQuota[]; applicableFeatures?: string[] }>(url)
      .toPromise()
      .then(
        (res: { quotas: CompanyQuota[]; applicableFeatures?: string[] }) => {
          const canAddSeats = this.checkSubscriptionQuota(
            res?.quotas ?? [],
            SubscriptionUpdateReason.seats
          );

          let serviceType = ServiceType.addSeats;
          if (
            !(res.applicableFeatures || []).includes(
              SubscriptionUpdateReason.seats
            )
          ) {
            serviceType = ServiceType.regularPlans;
          }

          return {
            value: canAddSeats,
            serviceType: serviceType,
          };
        }
      )
      .catch(() => {
        return { value: true, serviceType: ServiceType.regularPlans };
      });
  }

  async checkBillingDocumentsQuota(): Promise<void> {
    const url = `:API_BASE_URLsubscriptions/subscriptions`;
    const result = await this.http
      .get<{ quotas?: CompanyQuota[] }>(url)
      .toPromise();

    const canAdd = this.checkSubscriptionQuota(
      result?.quotas ?? [],
      SubscriptionUpdateReason.billing
    );

    if (canAdd) return;

    this.subscriptionViewService.openRegularPlansPage(
      SubscriptionUpdateReason.billing
    );
  }

  async canAddPlanSeats(): Promise<boolean> {
    try {
      const { value, serviceType } = await this.canAddSeats();
      if (value) return true;

      if (serviceType === ServiceType.addSeats) {
        this.subscriptionViewService.openAddSeatsModal();
      } else {
        this.subscriptionViewService.openRegularPlansPage(
          SubscriptionUpdateReason.seats,
          window.location.pathname
        );
      }

      return false;
    } catch {
      return false;
    }
  }

  async checkTrialSubscription(
    companyId: string,
    rootModule: string
  ): Promise<CheckTrialResponse> {
    if (!companyId || !rootModule) {
      return null;
    }

    try {
      const url = `:API_URL/subscription-manager/subscriptions/check-trial/${companyId}?rootModule=${rootModule}`;
      const response = await this.http.get<CheckTrialResponse>(url).toPromise();
      return response;
    } catch (error) {
      console.error('Error checking trial subscription:', error);
      return null;
    }
  }
}
